import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { bookingService } from "../services/bookingService";
import { today, addDays } from "../utils/date";
import { STATUS_COLORS, statusColor } from "../utils/statusColors";
import { TrendChart } from "../components/TrendChart";
import { DateField } from "../components/DateField";
import { Loader, Spinner } from "../components/Loader";

const bookingStatusLegend = [
  "Available",
  "Booked",
  "On-hold",
  "Checked-in",
  "Checked-out",
  "Cancelled",
  "Arrivals",
  "Departures",
].map((label) => ({ label, color: STATUS_COLORS[label.toLowerCase()] }));

export function Dashboard({ onTileClick, tenantId }) {
  const [selectedDate, setSelectedDate] = useState(today());
  const [guests, setGuests] = useState([]);
  const [departures, setDepartures] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [trend, setTrend] = useState({ labels: [], values: [] });
  const [yesterdayArrivals, setYesterdayArrivals] = useState(0);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const d = selectedDate;
    let cancelled = false;
    setLoading(true);
    setFailed(false);
    const settle = () => { if (!cancelled) setLoading(false); };
    const report = (opts) => bookingService.getReportWithTenant(opts, tenantId).catch((err) => { console.error("Dashboard report error:", err); setFailed(true); return []; });
    report({ start: d, end: d, roomId: "" })
      .then((rows) => {
        if (cancelled) return;
        setGuests(rows.filter((b) => b.checkInDate === d));
        setDepartures(rows.filter((b) => b.checkOutDate === d));
      })
      .finally(settle);
    bookingService.getAvailability(d, addDays(d, 1), undefined, tenantId)
      .then((rows) => { if (!cancelled) setBookings(rows); })
      .catch((err) => { console.error("Dashboard availability error:", err); if (!cancelled) setFailed(true); })
      .finally(settle);
    report({ start: addDays(d, -13), end: d, roomId: "" })
      .then((rows) => {
        if (cancelled) return;
        const counts = {};
        rows.forEach((b) => { counts[b.checkInDate] = (counts[b.checkInDate] || 0) + 1; });
        const labels = [];
        const values = [];
        for (let i = 0; i < 14; i++) {
          const day = addDays(d, -(13 - i));
          labels.push(day);
          values.push(counts[day] || 0);
        }
        setTrend({ labels, values });
      })
      .finally(settle);
    const y = addDays(d, -1);
    report({ start: y, end: y, roomId: "" })
      .then((rows) => { if (!cancelled) setYesterdayArrivals(rows.filter((b) => b.checkInDate === y).length); })
      .finally(settle);
    return () => { cancelled = true; };
  }, [selectedDate]);

  const availableRooms = bookings.filter((b) => String(b.status || "").toLowerCase() === "available");
  const isToday = selectedDate === today();

  const cards = [
    { label: isToday ? "Today's Arrivals" : "Arrivals", value: guests.length, color: "#2563eb", type: "arrivals", sub: `${yesterdayArrivals} from ${formatDate(addDays(selectedDate, -1))}` },
    { label: isToday ? "Today's Departures" : "Departures", value: departures.length, color: "#7c3aed", type: "departures", sub: "Expected Check-outs" },
    { label: "Available Room's", value: availableRooms.length, color: "#059669", type: "available", sub: "Ready to Sell" },
  ];

  function formatDate(d) {
    if (!d) return "";
    const dt = new Date(`${d}T00:00:00`);
    return Number.isNaN(dt.getTime()) ? d : dt.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
  }

  const pageTitle = (todayLabel, otherLabel) => (isToday ? todayLabel : `${otherLabel} · ${formatDate(selectedDate)}`);

  return (
    <section className="stack" aria-busy={loading || undefined}>
      <div className="toolbar">
        <label className="date-label">
          Date
          <DateField
            value={selectedDate}
            onChange={setSelectedDate}
            showWeekday
            ariaLabel="Dashboard date"
          />
        </label>
        <button className="secondary" disabled={loading} onClick={() => setSelectedDate(addDays(selectedDate, -1))}><ChevronLeft size={16} />Prev</button>
        <button className="secondary" disabled={loading} onClick={() => setSelectedDate(addDays(selectedDate, 1))}>Next<ChevronRight size={16} /></button>
        <button className="primary" disabled={loading} onClick={() => setSelectedDate(today())}>
          {loading && <Spinner size={14} />}
          Today
        </button>
      </div>

      {loading ? (
        <Loader label="Loading dashboard..." />
      ) : failed ? (
        <div className="table-empty">Could not load dashboard data. Try again.</div>
      ) : (
        <>
      <section className="grid cards">
        {cards.map((card) => {
          const inner = (
            <>
              <span>{card.label}</span>
              <strong>{card.value}</strong>
              <em className="metric-sub">{card.sub}</em>
            </>
          );
          return (
            <button className="metric" key={card.label} style={{ "--status": card.color }} onClick={() => onTileClick(card.type, selectedDate)}>
              {inner}
            </button>
          );
        })}
      </section>

      <div className="dashboard-columns">
        <div className="panel">
          <div className="table-header">
            <h2 className="table-title">{pageTitle("Today's Arrivals", "Arrivals")}</h2>
            <button className="primary" onClick={() => onTileClick("arrivals", selectedDate)}>View All</button>
          </div>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Guest</th>
                  <th>Room</th>
                  <th>Time</th>
                  <th style={{ textAlign: "left" }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {guests.length === 0 ? (
                  <tr>
                    <td colSpan={4} style={{ textAlign: "center", padding: 24, color: "#94a3b8" }}>No arrivals for this date.</td>
                  </tr>
                ) : (
                  guests.map((g) => (
                    <tr key={g.id}>
                      <td>{g.guestName}</td>
                      <td>{g.roomName}</td>
                      <td>{formatDate(g.checkInDate)}</td>
                      <td><span className="status" style={{ background: `${statusColor(g.status)}22`, color: statusColor(g.status) }}>{g.status}</span></td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="panel">
          <div className="table-header">
            <h2 className="table-title">{pageTitle("Today's Bookings", "Bookings")}</h2>
            <button className="primary" onClick={() => onTileClick("arrivals", selectedDate)}>View All</button>
          </div>
          <div className="booking-legend">
            {bookingStatusLegend.map((s) => (
              <span className="booking-legend-item" key={s.label}>
                <i className="booking-legend-dot" style={{ background: s.color }} />
                {s.label}
              </span>
            ))}
          </div>
          <div className="booking-tiles">
            {bookings.length === 0 ? (
              <div className="booking-tiles-empty">No bookings for this date.</div>
            ) : (
              bookings.map((g) => {
                const color = statusColor(g.status);
                return (
                  <article className="booking-tile" key={g.id} style={{ "--status": color }}>
                    <span className="booking-tile-room-name">{g.roomName || (g.id ? `Room ${g.id}` : g.roomType)}</span>
                  </article>
                );
              })
            )}
          </div>
        </div>
      </div>

      <div className="panel">
        <div className="table-header">
          <h2 className="table-title">Booking Trend</h2>
          <span className="search-count">Last 14 days</span>
        </div>
        <TrendChart labels={trend.labels} values={trend.values} />
      </div>
        </>
      )}
    </section>
  );
}